import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { supabase } from '@/integrations/supabase/client';
import { type DeskContentType, isDeskContentType } from '@/lib/desk/content-type';
import { type ProductionStage } from '@/lib/desk/stage';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import { logger } from '@/utils/logger';

const PRODUCTION_STAGES: readonly ProductionStage[] = ['idea', 'drafting', 'made'];
const isProductionStage = (value: unknown): value is ProductionStage =>
  typeof value === 'string' && (PRODUCTION_STAGES as readonly string[]).includes(value);

/**
 * A dated marketing moment the operator typed themselves — "Q3 blog goes
 * live", "Film Mark's interview", "Booth build day". Not a Mora post, not an
 * AI planned slot, not a Reach event.
 *
 * Deliberately NOT entitlement-gated the way `useReachCalendarEvents` is.
 * Reach is a private consulting tool behind `isReachEnabledForWorkspace`; a
 * moment is a plain calendar feature every workspace gets, and gating it would
 * make the Publishing calendar a different product for two kinds of customer
 * for no reason either of them could see.
 */
export interface CalendarMoment {
  id: string;
  title: string;
  notes: string | null;
  /**
   * Where this lands once it is public — a blog post or service page on a
   * surface Mora does not publish to. Was typed into `notes` as prose until
   * 2026-09-09, because the notes placeholder asked for it there; a URL in
   * prose cannot be opened, joined, or checked against the domain an ad points
   * at. NOT a source URL — this is where the thing is GOING, not where it
   * came FROM.
   */
  url: string | null;
  /**
   * Inclusive local calendar day, "YYYY-MM-DD". `null` means nobody has put
   * this on a day yet — a real state, not a gap: "finalize the case study
   * video" is real work before it has a date. An undated moment never
   * matches `useCalendarMoments`' date-window query (a `null` fails both the
   * `lte`/`gte` comparisons by Postgres's own NULL semantics), so it will
   * not appear on the month grid — there is currently no separate surface
   * for undated moments; this only stops the dialog forcing a fake date.
   */
  startsOn: string | null;
  /**
   * Inclusive local calendar day. `null` alongside a `null` `startsOn`
   * (undated), or alongside a real `startsOn` for a single-day moment — a
   * dedicated "same day" state doesn't exist, so a reader that needs an end
   * treats `endsOn ?? startsOn` as the single day.
   */
  endsOn: string | null;
  /**
   * What this moment IS, in the founder's own desk vocabulary
   * (`DESK_CONTENT_TYPES`). Nullable, no default: an existing moment has no
   * type and that is a fact, not a gap.
   *
   * 🔴 THIS COLUMN EXISTED FOR TWO DAYS WITH NO WRITER AND NO READER HERE.
   * It was added 2026-09-07 and `server/api/desk/_build-payload.ts` reads it as
   * `momentType`, but this hook never selected it and the dialog never set it —
   * so every moment reached the Content Desk with `momentType: null`, fell
   * through every branch of `deriveContentType`, and rendered as **"Proposal"**,
   * whose reader-facing meaning is "Nothing has been written yet — you are
   * deciding whether it should be." Printed under "Film Mark's interview".
   */
  contentType: DeskContentType | null;
  /**
   * The operator's own opt-in — whether this moment appears on the
   * workspace's Content Desk. Defaults `true` at the database, matching the
   * unconditional visibility `_load-desk.ts` gave every moment before this
   * column existed (see its own comment, and `_build-payload.ts`'s
   * `TODO(desk-visibility-flag)`, which named exactly this column).
   */
  visibleOnDesk: boolean;
  /** A single reference file — "here's the draft", "here's the outline". */
  attachment: CalendarMomentAttachment | null;
  /**
   * How far along the work is, in the desk's own `ProductionStage`
   * vocabulary. Nullable — "the operator did not say" is a real answer, same
   * pattern as `contentType`. Without it, the Content Desk always rendered
   * a moment as "Proposed" (`buildItemFromMoment`'s neutral default), even
   * one whose footage was already edited.
   */
  productionStage: ProductionStage | null;
}

/** `name` for display, `path` to sign a read URL or delete the blob. */
export interface CalendarMomentAttachment {
  name: string;
  path: string;
}

interface CalendarMomentRow {
  id: string;
  title: string;
  notes: string | null;
  url: string | null;
  starts_on: string | null;
  ends_on: string | null;
  content_type: string | null;
  visible_on_desk: boolean;
  attachment_name: string | null;
  attachment_path: string | null;
  production_stage: string | null;
}

/** The DB CHECK allows exactly DESK_CONTENT_TYPES; anything else is bad data. */
const readContentType = (value: string | null): DeskContentType | null =>
  isDeskContentType(value) ? value : null;

const toMoment = (row: CalendarMomentRow): CalendarMoment => ({
  id: row.id,
  title: row.title,
  notes: row.notes,
  url: row.url,
  startsOn: row.starts_on,
  endsOn: row.ends_on,
  contentType: readContentType(row.content_type),
  visibleOnDesk: row.visible_on_desk,
  attachment:
    row.attachment_name && row.attachment_path
      ? { name: row.attachment_name, path: row.attachment_path }
      : null,
  productionStage: isProductionStage(row.production_stage) ? row.production_stage : null,
});

const MOMENT_COLUMNS =
  'id, title, notes, url, starts_on, ends_on, content_type, visible_on_desk, attachment_name, attachment_path, production_stage';

export interface UseCalendarMomentsOptions {
  /** "YYYY-MM-DD", inclusive. */
  startDate: string;
  /** "YYYY-MM-DD", inclusive. */
  endDate: string;
  enabled?: boolean;
}

export const CALENDAR_MOMENTS_QUERY_KEY = 'calendar-moments';

export function useCalendarMoments({
  startDate,
  endDate,
  enabled = true,
}: UseCalendarMomentsOptions) {
  const { user, loading: authLoading } = useAuth();
  const { activeKit, isActiveKitPending } = useBrandKits();
  const activeKitId = activeKit?.id ?? null;

  return useQuery<CalendarMoment[]>({
    queryKey: [CALENDAR_MOMENTS_QUERY_KEY, user?.id ?? 'anon', activeKitId, startDate, endDate],
    queryFn: async (): Promise<CalendarMoment[]> => {
      if (!user?.id || !activeKitId) return [];

      // Same coarse-then-exact shape as `useReachCalendarEvents`: filter
      // server-side on "starts on or before the window's end", then keep only
      // the rows whose interval actually overlaps. PostgREST cannot express
      // `ends_on >= startDate AND starts_on <= endDate` as one overlap
      // predicate, and a moment count per workspace is dozens, not thousands.
      const { data, error } = await supabase
        .from('calendar_moments')
        .select(MOMENT_COLUMNS)
        .eq('workspace_id', activeKitId)
        .lte('starts_on', endDate)
        .gte('ends_on', startDate)
        .order('starts_on', { ascending: true });

      if (error) {
        logger.error('[useCalendarMoments] failed to load moments', error.message);
        throw new Error(error.message);
      }

      return (data as CalendarMomentRow[]).map(toMoment);
    },
    enabled:
      Boolean(user?.id) && !authLoading && !isActiveKitPending && Boolean(activeKitId) && enabled,
    staleTime: 60_000,
  });
}

export interface CalendarMomentInput {
  title: string;
  notes?: string | null;
  /** Empty string and null both mean "not given" — normalised at the writer. */
  url?: string | null;
  /** Omit or `null` for an undated moment — nobody has put this on a day yet. */
  startsOn?: string | null;
  /** Omit for a single-day moment; the writer stores `startsOn`, never a
   *  guessed different day. `null`/omitted with no `startsOn` either stays
   *  undated. */
  endsOn?: string | null;
  /** Optional. Null means "the operator did not say", never a guessed default. */
  contentType?: DeskContentType | null;
  /** Optional. Null means "the operator did not say" — see `CalendarMoment.productionStage`. */
  productionStage?: ProductionStage | null;
  /** Omit to default true — see `CalendarMoment.visibleOnDesk`. */
  visibleOnDesk?: boolean;
  /** The dialog's current attachment state, like every other field here —
   *  `null`/omitted both mean "no attachment", never "leave unchanged". */
  attachment?: CalendarMomentAttachment | null;
}

/**
 * Create / update / delete, sharing one invalidation. Split from the reader so
 * a surface that only displays moments does not pull three mutations it will
 * never call.
 *
 * 🔴 `owner_id` is `resolveOwnerId(user.id)`, never the raw auth id — this is
 * an account-scoped table (composite FK to `brand_kits(id, user_id)`, and
 * `brand_kits.user_id` is the account OWNER). A team member writing their own
 * auth id here would insert a row that the RLS policy's `with check` arm
 * rejects, and vitest mocks Supabase so no test in this repo would catch it.
 */
export function useCalendarMomentMutations() {
  const { user } = useAuth();
  const { activeKit } = useBrandKits();
  const activeKitId = activeKit?.id ?? null;
  const queryClient = useQueryClient();

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: [CALENDAR_MOMENTS_QUERY_KEY] });
  };

  const create = useMutation({
    mutationFn: async (input: CalendarMomentInput): Promise<CalendarMoment> => {
      if (!user?.id) throw new Error('Not signed in.');
      if (!activeKitId) throw new Error('No workspace selected.');
      const ownerId = await resolveOwnerId(user.id);

      const { data, error } = await supabase
        .from('calendar_moments')
        .insert({
          workspace_id: activeKitId,
          owner_id: ownerId,
          title: input.title.trim(),
          notes: input.notes?.trim() || null,
          url: input.url?.trim() || null,
          starts_on: input.startsOn || null,
          // A single-day moment is a one-day interval, not an open one, and
          // an undated moment has no end either. Only a real `startsOn`
          // gives `endsOn` something to default to.
          ends_on: input.endsOn || input.startsOn || null,
          content_type: input.contentType ?? null,
          production_stage: input.productionStage ?? null,
          visible_on_desk: input.visibleOnDesk ?? true,
          attachment_name: input.attachment?.name ?? null,
          attachment_path: input.attachment?.path ?? null,
        })
        .select(MOMENT_COLUMNS)
        .single();

      if (error) {
        logger.error('[useCalendarMomentMutations] create failed', error.message);
        throw new Error(error.message);
      }
      return toMoment(data as CalendarMomentRow);
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, ...input }: CalendarMomentInput & { id: string }): Promise<void> => {
      const { error } = await supabase
        .from('calendar_moments')
        .update({
          title: input.title.trim(),
          notes: input.notes?.trim() || null,
          url: input.url?.trim() || null,
          starts_on: input.startsOn || null,
          ends_on: input.endsOn || input.startsOn || null,
          content_type: input.contentType ?? null,
          production_stage: input.productionStage ?? null,
          visible_on_desk: input.visibleOnDesk ?? true,
          attachment_name: input.attachment?.name ?? null,
          attachment_path: input.attachment?.path ?? null,
        })
        .eq('id', id);

      if (error) {
        logger.error('[useCalendarMomentMutations] update failed', error.message);
        throw new Error(error.message);
      }
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('calendar_moments').delete().eq('id', id);
      if (error) {
        logger.error('[useCalendarMomentMutations] delete failed', error.message);
        throw new Error(error.message);
      }
    },
    onSuccess: invalidate,
  });

  /**
   * Its own bucket, not `plan-documents` (the closest existing "attach a file
   * to an object" precedent, `campaign_documents`) — that bucket, and `media`,
   * carry NO `storage.objects` RLS policy at all (verified live, 2026-09-16),
   * so an authenticated upload to either is denied by the enabled RLS with no
   * matching policy. This mirrors `reach-imports`' owner+workspace path
   * convention instead, which does have working policies.
   */
  const ATTACHMENT_BUCKET = 'calendar-moment-attachments';

  const uploadAttachment = useMutation({
    mutationFn: async ({
      momentKey,
      file,
    }: {
      /** Stable for the dialog's lifetime — the moment's own id when editing,
       *  a fresh client uuid when creating (the row has no id yet). */
      momentKey: string;
      file: File;
    }): Promise<CalendarMomentAttachment> => {
      if (!user?.id) throw new Error('Not signed in.');
      if (!activeKitId) throw new Error('No workspace selected.');
      const ownerId = await resolveOwnerId(user.id);

      const sanitized = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-160);
      const path = `${ownerId}/${activeKitId}/${momentKey}/${crypto.randomUUID()}-${sanitized}`;

      const { error } = await supabase.storage.from(ATTACHMENT_BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type,
      });
      if (error) {
        logger.error('[useCalendarMomentMutations] attachment upload failed', error.message);
        throw new Error(error.message);
      }
      return { name: file.name, path };
    },
  });

  /** Best-effort — an orphaned private blob costs nothing and is never listed anywhere. */
  const removeAttachment = useMutation({
    mutationFn: async (path: string): Promise<void> => {
      await supabase.storage
        .from(ATTACHMENT_BUCKET)
        .remove([path])
        .catch(() => undefined);
    },
  });

  /** Signed, not public — the bucket is private like every other document store in this app. */
  const getAttachmentUrl = async (path: string): Promise<string | null> => {
    const { data, error } = await supabase.storage
      .from(ATTACHMENT_BUCKET)
      .createSignedUrl(path, 300);
    if (error || !data) return null;
    return data.signedUrl;
  };

  return { create, update, remove, uploadAttachment, removeAttachment, getAttachmentUrl };
}
